import { useEffect } from 'react';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://rwa99.cn';

interface CourseData {
  id: string;
  title: string;
  subtitle: string;
  level: string;
  duration: string;
  lessons: number;
  students: string;
  price: string;
  badge: string;
  categoryLabel: string;
  highlights: string[];
  modules: Array<{ title: string; lessons: string[] }>;
}

interface CourseDetailSEOHeadProps {
  course: CourseData;
}

export default function CourseDetailSEOHead({ course }: CourseDetailSEOHeadProps) {
  useEffect(() => {
    if (!course) return;

    const pageUrl = `${SITE_URL}/courses/${course.id}`;
    const totalLessons = course.modules.reduce(
      (sum, m) => sum + m.lessons.length,
      0
    );

    // --- Page title & meta ---
    document.title = `${course.title} | RWA99课程中心 — ${course.subtitle}`;

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(
        `meta[${attr}="${name}"]`
      ) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    const descriptionText = `${course.title} — ${course.subtitle}。${course.level}难度，${course.duration}，${totalLessons}节课。核心内容：${course.highlights.slice(0, 3).join('、')}。RWA99专业培训，业内专家主讲，永久有效，${course.price}。`;

    setMeta('description', descriptionText);
    setMeta(
      'keywords',
      `${course.title},${course.categoryLabel},RWA培训,区块链课程,RWA99,${course.highlights.slice(0, 2).join(',')}`
    );
    setMeta('robots', 'index, follow');
    setMeta('og:title', `${course.title} | RWA99课程`, true);
    setMeta('og:description', `${course.subtitle} — ${course.level}难度，${course.duration}，${totalLessons}节课，${course.price}。`, true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', pageUrl, true);
    setMeta('og:site_name', 'RWA99.cn', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', `${course.title} | RWA99课程`);
    setMeta('twitter:description', `${course.subtitle} — ${course.level}难度，${course.price}。`);

    // --- Schema.org JSON-LD ---
    const existing = document.querySelectorAll('script[data-course-detail-jsonld]');
    existing.forEach((el) => el.remove());

    const syllabusSections = course.modules.map((mod) => ({
      '@type': 'Syllabus',
      name: mod.title,
      description: mod.lessons.join('；'),
      timeRequired: `PT${Math.ceil(mod.lessons.length * 0.5)}H`,
    }));

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: `${course.title} | RWA99课程中心`,
        description: descriptionText,
        inLanguage: 'zh-CN',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        publisher: { '@id': `${SITE_URL}/#organization` },
        datePublished: '2026-01-01',
        dateModified: '2026-03-29',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
            {
              '@type': 'ListItem',
              position: 2,
              name: '课程中心',
              item: `${SITE_URL}/courses`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: course.title,
              item: pageUrl,
            },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Course',
        '@id': `${pageUrl}#course`,
        url: pageUrl,
        name: course.title,
        description: `${course.subtitle}。${course.highlights.join('；')}。`,
        provider: {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: 'RWA99.cn',
          url: SITE_URL,
        },
        educationalLevel: course.level,
        timeRequired: `PT${parseInt(course.duration)}H`,
        numberOfCredits: totalLessons,
        teaches: course.highlights,
        hasCourseInstance: {
          '@type': 'CourseInstance',
          courseMode: 'online',
          inLanguage: 'zh-CN',
          courseSchedule: {
            '@type': 'Schedule',
            repeatFrequency: 'P1W',
          },
        },
        offers: {
          '@type': 'Offer',
          price: course.price.replace('¥', ''),
          priceCurrency: 'CNY',
          availability: 'https://schema.org/PreOrder',
          validFrom: '2026-01-01',
          seller: {
            '@type': 'Organization',
            name: 'RWA99.cn',
          },
          priceValidUntil: '2026-12-31',
          refundPolicy: {
            '@type': 'RefundTypeEnumeration',
            name: '7天无理由退款',
          },
        },
        coursePrerequisites: course.level === '入门' ? '无需技术背景，具备基本金融常识即可' : '建议先完成区块链基础课程',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          reviewCount: course.students.replace(',', ''),
          bestRating: '5',
          worstRating: '1',
        },
        syllabusSections,
        inLanguage: 'zh-CN',
        isAccessibleForFree: false,
        image: `${SITE_URL}/favicon.png`,
      },
    ];

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-course-detail-jsonld', 'true');
      script.textContent = JSON.stringify(schema, null, 2);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-course-detail-jsonld]').forEach((el) =>
        el.remove()
      );
    };
  }, [course]);

  return null;
}
